import React, { HTMLAttributes } from "react";
import ReactDOM from "react-dom";
import debounce from "lodash.debounce";
import { inject, observer } from "mobx-react";
import * as PropTypes from "prop-types";
import _ from "lodash";
import cx from "classnames";
import Cache from "core/cache/index";
/// Message Modules
import Helper from "helper/index";
import Config from "config";
import ImageControls from "component/atoms/imageScrollbar";
import "./DCMTagMarker.styl";
import {
  getEnabledElement,
  getEnabledElements,
  getEnabledElementById,
} from "component/toolManager/enabledElements.js";
import ElementViewConfigStore from "pages/viewer-dashboard/store/ElementViewConfigStore";
import ToolbarStore from "pages/viewer-dashboard/store/toolbarStore";
import calculateSUV from "component/util/calculateSUV.js";
import Color from "component/molecules/viewport/core/color/";
import { ColorscaleBar } from "component/molecules";
import { isActiveToolModeForElement } from "component/molecules/viewport/toolStates/setToolMode";
import makeKey from "core/cache/KeyGen";

/**
 * @description DCMTagOverlay UI Component Properties
 *
 * @interface IDCMTagOverlayProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IDCMTagOverlayProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * @description DICOM stack 데이터
   */
  stack: any;
  /**
   * @description 현재 마우스 좌표
   */
  mousePos?: any;
  /**
   * @description DICOM Stack에서 현재 출력되는 DICOM 영상의 Index
   */
  imageIndex: number;
  /**
   * @description 뷰포트 설정 정보
   */
  viewport: any;
  /**
   * @description DICOM stack 에 포함된 영상 갯수
   */
  numberOfFrames: number;
  show?: boolean;
  /**
   * @description 현재 DICOM Viewer 레이아웃 정보.
   * @deprecated
   */
  frameRange: any;

  number?: number;
  layoutString: string;
  value: string;
  levelPresetName: string;
  parentElement: any;
  isFocus: boolean;
  width: number;
  height: number;
  elementViewConfigStore: ElementViewConfigStore;
  toolbarStore: ToolbarStore;
}

/**
 * @description DCMTagOverlay UI Class
 *
 * @class DCMTagOverlay
 * @extends {React.PureComponent<IDCMTagOverlayProps, any>}
 */
@inject("elementViewConfigStore")
@inject("toolbarStore")
@observer
class DCMTagOverlay extends React.Component<IDCMTagOverlayProps, any> {
  static propTypes: PropTypes.InferProps<IDCMTagOverlayProps>;
  /**
   * Tag group div
   * @memberof DCMTagOverlay
   */
  topLeftTagDivGroup = null;
  topRightTagDivGroup = null;
  bottomLeftTagDivGroup = null;
  bottomRightTagDivGroup = null;
  /**
   * default property values
   *
   * @static
   * @memberof DCMTagOverlay
   */
  public static defaultProps = {
    stack: null,
    mousePos: null,
    value: "",
    levelPresetName: "",
    parentElement: null,
    isFocus: false,
    frameRange: { min: -1, max: Number.MAX_VALUE },
    show: true,
    style: {
      fontSize: "15px",
      transform: `scale(1)`,
      lineHeight: "11px",
    },
  };

  element: any = null;

  constructor(props: any) {
    super(props);
    this.state = {
      numImages: props.numImages || 0,
      mousePos: props.mousePos,
      value: "",
      style: props.style,
      prevWidthRatio: 0,
      prevHeightRatio: 0,
    };
  }

  componentWillUpdate(nextProps: any, nextState: any) {
    (this.state.numImages as any) = nextProps.numImages;
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (
      !(this.props.width === this.state.prevWidthRatio) ||
      !(this.props.height === this.state.prevHeightRatio) ||
      this.props.isFocus !== prevProps.isFocus
    ) {
      /** Max Tag Size */
      const baseFontSize = 15;
      const baseLineHeight = 26;
      if (Helper.Validator.isDefined(this.element) === false) {
        return;
      }
      const clientShortSideLength = Math.min(
        this.element.clientHeight,
        this.element.clientWidth
      );
      const ratio = Math.max(
        Math.min(clientShortSideLength / 30, baseFontSize) / baseFontSize,
        0.3
      );

      this.setState({
        prevWidthRatio: this.props.width,
        prevHeightRatio: this.props.height,
        style: {
          fontSize: baseFontSize + "px",
          transform: `scale(${ratio})`,
          lineHeight: baseLineHeight * ratio + "px",
        },
      });
    }
  }

  componentDidMount() {
    let element = ReactDOM.findDOMNode(this);
    if (element !== null) {
      element.addEventListener(
        "changedPointOfInterest",
        this.handleChangedPointOfInterest
      );
      window.addEventListener("resize", this.handleWindowResize);
    }
    /** Max Tag Size */
    const baseFontSize = 15;
    const baseLineHeight = 26;
    if (Helper.Validator.isDefined(this.element) === false) {
      return;
    }
    const clientShortSideLength = Math.min(
      this.element.clientHeight,
      this.element.clientWidth
    );
    const ratio = Math.max(
      Math.min(clientShortSideLength / 30, baseFontSize) / baseFontSize,
      0.3
    );

    this.setState({
      prevWidthRatio: this.props.width,
      prevHeightRatio: this.props.height,
      style: {
        fontSize: baseFontSize + "px",
        transform: `scale(${ratio})`,
        lineHeight: baseLineHeight * ratio + "px",
      },
    });
  }

  componentWillUnmount() {
    let element = ReactDOM.findDOMNode(this);
    if (element !== null) {
      element.removeEventListener(
        "changedPointOfInterest",
        this.handleChangedPointOfInterest
      );
      window.removeEventListener("resize", this.handleWindowResize);
    }
  }
  handleWindowResize = (event: any) => {
    // HACK : DCMView 크기 변화 발생시 DCMTag를 표시하는 폰트 크기를 변화시키기위해
    // 아래와 같이 setState를 실행하여 리-렌더링을 유도하게 하였음
    //this.setState({}); // 리사이즈시 속도 개선을위해 주석
    this.debouncedResize();
  };

  /**
   * @description 화면 크기 변경시, viewport 크기 변환
   * @method debouncedResize
   * @memberof Viewport
   */
  debouncedResize = debounce(() => {
    /** Max Tag Size */
    const baseFontSize = 15;
    const baseLineHeight = 26;
    if (Helper.Validator.isDefined(this.element) === false) {
      return;
    }
    const clientShortSideLength = Math.min(
      this.element.clientHeight,
      this.element.clientWidth
    );
    const ratio = Math.max(
      Math.min(clientShortSideLength / 30, baseFontSize) / baseFontSize,
      0.3
    );
    this.setState({
      style: {
        fontSize: baseFontSize + "px",
        transform: `scale(${ratio})`,
        lineHeight: baseLineHeight * ratio + "px",
      },
    });
  }, 100);

  handleChangedPointOfInterest = (event: any) => {
    this.setState({
      mousePos: { x: event.detail.x, y: event.detail.y },
      value: event.detail.value,
    });
  };

  handleToggleDicomTag() {
    this.forceUpdate();
  }

  handleNewMetadata() {
    let metadataCache = new Cache.Metadata();
    const numOfFrame = metadataCache.getNumOfFrame(this.props.stack);
    this.setState({
      numImages: numOfFrame,
    });
  }

  /**
   * @method isControlVisible
   * @description DCM 데이터가 한장이라도 있다면, DCMTagOverlay 를 출력
   * @memberof DCMTagOverlay
   */
  isControlVisible = () => {
    let controlVisible = this.getNumImages() >= 1 ? "controlsVisible" : "";
    return "imageViewerViewportOverlay noselect nodrag " + controlVisible;
    //return "";
  };

  /**
   * @method getFrameIndex
   * @description DICOM Stack에서 현재 출력중인 DICOM 영상의 인덱스 반환
   * @return DICOM Stack에서 현재 출력중인 DICOM 영상의 인덱스
   * @memberof DCMTagOverlay
   */
  getFrameIndex = () => {
    let frameIndex = 0;

    if (this.props.imageIndex !== undefined) {
      frameIndex = this.props.imageIndex;
    }

    return frameIndex;
  };

  /**
   * @method getWindowWidth
   * @description DICOM Stack의 windowWidth 반환
   * @return DICOM Stack의 windowWidth 반환
   * @todo DICOM Stack에서 현재 출력중인 DICOM 영상의 windowWidth 반환하도록 수정
   * @memberof DCMTagOverlay
   */
  getWindowWidth() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const windowWidth = metadataCache.getWindowWidth(this.props.stack);

    if (typeof windowWidth === undefined) {
      return "";
    }

    if (windowWidth === undefined || windowWidth === null) {
      return "";
    }

    return windowWidth;
  }

  /**
   * @method getWindowCenter
   * @description DICOM Stack의 WindowCenter 반환
   * @return DICOM Stack의 WindowCenter 반환
   * @todo DICOM Stack에서 현재 출력중인 DICOM 영상의 WindowCenter 반환하도록 수정
   * @memberof DCMTagOverlay
   */
  getWindowCenter() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const windowCenter = metadataCache.getWindowCenter(this.props.stack);

    if (typeof windowCenter === undefined) {
      return "";
    }

    if (windowCenter === undefined || windowCenter === null) {
      return "";
    }

    return windowCenter;
  }

  /**
   * @method getWWWC
   * @description DICOM Stack의 WW/WC 반환
   * @return DICOM Stack의 WW/WC 반환
   * @todo DICOM Stack에서 현재 출력중인 DICOM 영상의 WW/WC 반환하도록 수정
   * @memberof DCMTagOverlay
   */
  getWWWC() {
    let enabledElement = getEnabledElementById(this.props.number);

    const viewport = getEnabledElementById(this.props.number).viewport;

    if (!viewport) {
      return "";
    }
    if (viewport.voi === null || viewport.voi === undefined) {
      return "";
    }

    let windowWidth: any = 0.0;
    let windowCenter: any = 0.0;

    if (
      viewport.voi.windowWidth === null ||
      viewport.voi.windowWidth === undefined
    ) {
      windowWidth = this.getWindowWidth();
    } else {
      windowWidth = viewport.voi.windowWidth;
    }
    if (
      viewport.voi.windowCenter === null ||
      viewport.voi.windowCenter === undefined
    ) {
      windowCenter = this.getWindowCenter();
    } else {
      windowCenter = viewport.voi.windowCenter;
    }

    if (
      !windowWidth ||
      !windowCenter ||
      windowWidth === "" ||
      windowCenter === ""
    ) {
      return "";
    }
    return (
      //"WL : " + windowCenter.toFixed(0) + " WW : " + windowWidth.toFixed(0)
      "WL : " + windowCenter.toFixed(0) + " WW : " + windowWidth.toFixed(0)
    );
  }

  /**
   * @method getZoom
   * @description 현재 뷰포트의 확대 비율 반환
   * @return 현재 뷰포트의 확대 비율
   * @memberof DCMTagOverlay
   */

  getZoom() {
    let viewport = this.props.viewport;
    if (!viewport) {
      return "";
    }

    return viewport.scale * 100.0;
  }

  /**
   * @method getPatientName
   * @description 현재 출력중인 DICOM Stack의 환자 이름 반환
   * @return 현재 출력중인 DICOM Stack의 환자 이름
   * @memberof DCMTagOverlay
   */
  getPatientName() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const patientName = metadataCache.getPatientName(this.props.stack);

    if (typeof patientName === undefined) {
      return "";
    }

    if (patientName === undefined || patientName === null) {
      return "";
    }

    return patientName;
  }

  /**
   * @method getPatientId
   * @description 현재 출력중인 DICOM Stack의 환자 ID 반환
   * @return 현재 출력중인 DICOM Stack의 환자 ID
   * @memberof DCMTagOverlay
   */
  getPatientId() {
    let data = this.props.stack;

    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const patientID = metadataCache.getPatientId(this.props.stack);

    if (typeof patientID === undefined) {
      return "";
    }

    if (patientID === undefined || patientID === null) {
      return "";
    }

    return patientID;
  }

  /**
   * @method getPatientBirthDate
   * @description 현재 출력중인 DICOM Stack의 환자 생일 반환
   * @return 현재 출력중인 DICOM Stack의 환자 생일
   * @memberof DCMTagOverlay
   */
  getPatientBirthDate() {
    let data = this.props.stack;

    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const patientBirthdate = metadataCache.getPatientBirthDate(
      this.props.stack
    );

    if (typeof patientBirthdate === undefined) {
      return "";
    }

    if (patientBirthdate === undefined || patientBirthdate === null) {
      return "";
    }

    return patientBirthdate;
  }

  /**
   * @method getPatientSex
   * @description 현재 출력중인 DICOM Stack의 환자 성별 반환
   * @return 현재 출력중인 DICOM Stack의 환자 성별
   * @memberof DCMTagOverlay
   */
  getPatientSex() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const patientSex = metadataCache.getPatientSex(this.props.stack);

    if (typeof patientSex === undefined) {
      return "";
    }

    if (patientSex === undefined || patientSex === null) {
      return "";
    }

    return patientSex;
  }

  /**
   * @method getStudyDate
   * @description 현재 출력중인 DICOM Stack의 스터디 날짜 반환
   * @return 현재 출력중인 DICOM Stack의 스터디 날짜
   * @memberof DCMTagOverlay
   */
  getStudyDate() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const studyDate = metadataCache.getStudyDate(this.props.stack);
    if (typeof studyDate === undefined) {
      return "";
    }

    if (studyDate === undefined || studyDate === null) {
      return "";
    }

    return studyDate;
  }

  /**
   * @method getStudyTime
   * @description 현재 출력중인 DICOM Stack의 스터디 시간 반환
   * @return 현재 출력중인 DICOM Stack의 스터디 시간
   * @memberof DCMTagOverlay
   */
  getStudyTime() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const studyTime = metadataCache.getStudyTime(this.props.stack);
    if (typeof studyTime === undefined) {
      return "";
    }

    if (studyTime === undefined || studyTime === null) {
      return "";
    }

    return studyTime;
  }

  /**
   * @method getStudyDescription
   * @description 현재 출력중인 DICOM Stack의 StudyDescription 반환
   * @return 현재 출력중인 DICOM Stack의 StudyDescription
   * @memberof DCMTagOverlay
   */
  getStudyDescription() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const studyDescription = metadataCache.getStudyDescription(
      this.props.stack
    );
    if (typeof studyDescription === undefined) {
      return "";
    }

    if (studyDescription === undefined || studyDescription === null) {
      return "";
    }

    return studyDescription;
  }

  /**
   * @method getSeriesDescription
   * @description 현재 출력중인 DICOM Stack의 SeriesDescription 반환
   * @return 현재 출력중인 DICOM Stack의 SeriesDescription
   * @memberof DCMTagOverlay
   */
  getSeriesDescription() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const seriesDescription = metadataCache.getSeriesDescription(
      this.props.stack
    );
    if (typeof seriesDescription === undefined) {
      return "";
    }

    if (seriesDescription === undefined || seriesDescription === null) {
      return "";
    }

    return seriesDescription;
  }

  /**
   * @method getSeriesNumber
   * @description 현재 출력중인 DICOM Stack의 시리즈 번호 반환
   * @return 현재 출력중인 DICOM Stack의 시리즈 번호
   * @memberof DCMTagOverlay
   */
  getSeriesNumber() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const seriesNumber = metadataCache.getSeriesNumber(this.props.stack);
    if (typeof seriesNumber === undefined) {
      return "";
    }

    if (seriesNumber === undefined || seriesNumber === null) {
      return "";
    }

    return seriesNumber;
  }

  getPixelSpacingGuideTextColor() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let frameIndex = 0;
    if (this.props.imageIndex !== undefined) {
      frameIndex = this.props.imageIndex;
    } else {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const pixelSpacing = metadataCache.getPixelSpacing(
      this.props.stack,
      frameIndex
    );

    const isUseApproximatePixelSpacing = metadataCache.isUseApproximatePixelSpacing(
      this.props.stack,
      frameIndex
    );

    if (
      typeof pixelSpacing !== undefined &&
      pixelSpacing !== undefined &&
      pixelSpacing !== null &&
      isUseApproximatePixelSpacing === false
    ) {
      return "";
    }

    if (isUseApproximatePixelSpacing === true) {
      return "green-color";
    } else {
      return "red-color";
    }
  }

  checkPixelSpacing() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let frameIndex = 0;
    if (this.props.imageIndex !== undefined) {
      frameIndex = Math.max(this.props.imageIndex, 0);
    } else {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const modality = metadataCache.getModality(data);
    const frame = metadataCache.getFrame(data, frameIndex);
    if (!_.get(frame, "pixelSpacing") === true) {
      return "";
    }
    const pixelSpacing = Helper.DICOM.getPixelSpacingByModality(
      modality,
      frame
    );

    const hasPixelSpacing = frame.isExistPixelSpacing;
    const hasReconstructionDiameter = frame.isExistReconstructionDiameter;

    let hasPhysicalDelta = true;

    const units = Helper.DICOM.getPhysicalDirectionUnit(frame);
    if (units.unitX === -1 || units.unitY === -1) {
      hasPhysicalDelta = false;
    }

    const isUseApproximatePixelSpacing = metadataCache.isUseApproximatePixelSpacing(
      this.props.stack,
      frameIndex
    );

    if (
      (hasPhysicalDelta === true ||
        hasPixelSpacing === true ||
        hasReconstructionDiameter === true) &&
      (pixelSpacing as any)["x"] !== 0 &&
      (pixelSpacing as any)["y"] !== 0 &&
      isUseApproximatePixelSpacing === false
    ) {
      return "";
    }

    if (isUseApproximatePixelSpacing === true) {
      return "PixelSpacing 태그 부재로 인한 대체값 사용";
    } else {
      return "PixelSpacing 태그 부재로 인한 Measure 사용 불가";
    }
  }

  /**
   * @method getThickness
   * @description 현재 출력중인 DICOM Stack의 sliceThickness 반환
   * @return 현재 출력중인 DICOM Stack의 sliceThickness
   * @memberof DCMTagOverlay
   */
  getThickness() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const sliceThickness = metadataCache.getThickness(this.props.stack);
    if (typeof sliceThickness === undefined) {
      return "";
    }

    if (sliceThickness === undefined || sliceThickness === null) {
      return "";
    }

    return sliceThickness;
  }

  /**
   * @method getLocation
   * @description 현재 출력중인 DICOM Stack의 sliceLocation 반환
   * @return 현재 출력중인 DICOM Stack의 sliceLocation
   * @memberof DCMTagOverlay
   */

  getLocation() {
    // Displays Slice Location (0020,1041), if present.
    // - Otherwise, displays Table Position (0018,9327)
    // - TODO: Otherwise, displays a value derived from Image Position (Patient) (0020,0032)

    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    let frameIndex = 0;
    if (this.props.imageIndex !== undefined) {
      frameIndex = this.props.imageIndex;
    } else {
      return "";
    }
    const sliceLocation =
      -1 * metadataCache.getSliceLocation(this.props.stack, frameIndex);
    const tablePosition = metadataCache.getTablePosition(this.props.stack);
    const imagePositionPatient = metadataCache.getImagePositionPatient(
      this.props.stack,
      frameIndex
    );
    let imagePositionString = "";
    if (imagePositionPatient !== undefined && imagePositionPatient !== null) {
      let lastIdx = imagePositionPatient.length - 1;
      return imagePositionPatient[lastIdx];
    }

    if (typeof sliceLocation === undefined) {
      return "";
    }

    if (sliceLocation === undefined || sliceLocation === null) {
      return "";
    }

    if (Helper.Validator.isNumber(sliceLocation) === true) {
      return sliceLocation;
    }

    if (typeof tablePosition === undefined) {
      return "";
    }

    if (tablePosition === undefined || tablePosition === null) {
      return "";
    }

    if (tablePosition !== "") {
      return tablePosition;
    }

    return imagePositionPatient;
  }

  /**
   * @method getInstitutionName
   * @description 현재 출력중인 DICOM Stack의 InstitutionName 반환
   * @return 현재 출력중인 DICOM Stack의 InstitutionName
   * @memberof DCMTagOverlay
   */
  getInstitutionName() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const institutionName = metadataCache.getInstitutionName(this.props.stack);
    if (typeof institutionName === undefined) {
      return "";
    }

    if (institutionName === undefined || institutionName === null) {
      return "";
    }

    return institutionName;
  }

  /**
   * @method getInstitutionAddress
   * @description 현재 출력중인 DICOM Stack의 InstitutionAddress 반환
   * @return 현재 출력중인 DICOM Stack의 InstitutionAddress
   * @memberof DCMTagOverlay
   */
  getInstitutionAddress() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const institutionAddress = metadataCache.getInstitutionAddress(
      this.props.stack
    );
    if (typeof institutionAddress === undefined) {
      return "";
    }

    if (institutionAddress === undefined || institutionAddress === null) {
      return "";
    }

    return institutionAddress;
  }

  /**
   * @method getXRayTubeCurrent
   * @description 현재 출력중인 DICOM Stack의 XRayTube 반환
   * @return 현재 출력중인 DICOM Stack의 XRayTube
   * @memberof DCMTagOverlay
   */
  getXRayTubeCurrent() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    if (typeof data.XRayTubeCurrent === undefined) {
      return "";
    }

    if (data.XrayTubeCurrent === undefined || data.XrayTubeCurrent === null) {
      return "";
    }

    return data.XRayTubeCurrent + "mA";
  }

  /**
   * @method getKVP
   * @description 현재 출력중인 DICOM Stack의 KVP 반환
   * @return 현재 출력중인 DICOM Stack의 KVP
   * @memberof DCMTagOverlay
   */
  getKVP() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const KVP = metadataCache.getKVP(this.props.stack);
    if (typeof KVP === undefined) {
      return "";
    }

    if (KVP === undefined || KVP === null) {
      return "";
    }

    return KVP + "kV";
  }
  getAcquisitionTag() {
    const acquisitionDate = Helper.DateHelper.Format.formatDA(
      this.getAcquisitionDate(),
      "YYYY-MM-DD"
    );
    const acquisitionTime = Helper.DateHelper.Format.formatTM(
      this.getAcquisitionTime(),
      "a hh:mm:ss"
    );

    if (
      acquisitionDate !== "" &&
      acquisitionDate !== undefined &&
      acquisitionDate !== null
    ) {
      return acquisitionDate + " " + acquisitionTime;
    }

    const performedProcedureStepID = this.getPerformedProcedureStepID();
    if (
      performedProcedureStepID !== "" &&
      performedProcedureStepID !== undefined &&
      performedProcedureStepID !== null
    ) {
      return performedProcedureStepID;
    }

    const studyDate = this.getStudyDate();
    const studyTime = this.getStudyTime();
    if (
      (studyTime !== "" && studyTime !== undefined && studyTime !== null) ||
      (studyDate !== "" && studyDate !== undefined && studyDate !== null)
    ) {
      let formatStudyTime: any = "";
      if (studyTime.includes(".")) {
        formatStudyTime = Helper.DateHelper.Format.formatTM(
          studyTime,
          "a hh:mm:ss"
        );
      } else {
        formatStudyTime = Helper.DateHelper.Format.formatTM(
          studyTime,
          "a hh:mm:ss",
          "hh:mm:ss.SSS"
        );
      }

      return (
        Helper.DateHelper.Format.formatDA(studyDate, "YYYY-MM-DD") +
        " " +
        formatStudyTime
      );
    }

    return "";
  }
  getPerformedProcedureStepID() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let frameIndex = 0;
    if (this.props.imageIndex !== undefined) {
      frameIndex = this.props.imageIndex;
    } else {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const performedProcedureStepID = metadataCache.getPerformedProcedureStepID(
      this.props.stack,
      frameIndex
    );

    if (typeof performedProcedureStepID === undefined) {
      return "";
    }

    if (
      performedProcedureStepID === undefined ||
      performedProcedureStepID === null
    ) {
      return "";
    }

    return Helper.DateHelper.Format.formatDATM(
      performedProcedureStepID,
      "YYYY-MM-DD HH:MM:SS"
    );
  }
  /**
   * @method getAcquisitionDate
   * @description 현재 출력중인 DICOM Stack의 AcquisitionDate 반환
   * @return 현재 출력중인 DICOM Stack의 AcquisitionDate
   * @memberof DCMTagOverlay
   */
  getAcquisitionDate() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const frameIndex = this.getFrameIndex();
    //const acquisitionDate = metadataCache.getAcquisitionDate(this.props.stack);
    const acquisitionDate = metadataCache.getAcquisitionDateFromFrame(
      this.props.stack,
      frameIndex
    );

    if (typeof acquisitionDate === undefined) {
      return "";
    }

    if (acquisitionDate === undefined || acquisitionDate === null) {
      return "";
    }

    return acquisitionDate;
  }

  /**
   * @method getAcquisitionTime
   * @description 현재 출력중인 DICOM Stack의 AcquisitionTime 반환
   * @return 현재 출력중인 DICOM Stack의 AcquisitionTime
   * @memberof DCMTagOverlay
   */
  getAcquisitionTime() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const frameIndex = this.getFrameIndex();
    //const acquisitionTime = metadataCache.getAcquisitionTime(this.props.stack);
    const acquisitionTime = metadataCache.getAcquisitionTimeFromFrame(
      this.props.stack,
      frameIndex
    );
    if (typeof acquisitionTime === undefined) {
      return "";
    }

    if (acquisitionTime === undefined || acquisitionTime === null) {
      return "";
    }

    return acquisitionTime;
  }

  /**
   * @method getSpacingBetweenSlices
   * @description 현재 출력중인 DICOM Stack의 SpacingBetweenSlices 반환
   * @return 현재 출력중인 DICOM Stack의 SpacingBetweenSlices
   * @memberof DCMTagOverlay
   */
  getSpacingBetweenSlices() {
    // Displays Spacing Between Slices (0018,0088), if present.

    // TODO: Otherwise, displays a value derived from successive values
    // of Image Position (Patient) (0020,0032) perpendicular to
    // the Image Orientation (Patient) (0020,0037)
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const spacingBetweenSlices = metadataCache.getSpacingBetweenSlices(
      this.props.stack
    );
    if (typeof spacingBetweenSlices === undefined) {
      return "";
    }

    if (spacingBetweenSlices === undefined || spacingBetweenSlices === null) {
      return "";
    }

    return spacingBetweenSlices;
  }

  /**
   * @method getCompression
   * @description 현재 출력중인 DICOM Stack의 Compression 반환
   * @return 현재 출력중인 DICOM Stack의 Compression
   * @memberof DCMTagOverlay
   */
  getCompression() {
    // Displays whether or not lossy compression has been applied:
    //
    // - Checks Lossy Image Compression (0028,2110)
    // - If so, displays the value of Lossy Image Compression Ratio (0028,2112)
    //          and Lossy Image Compression Method (0028,2114)
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const lossyImageCompressionMethod = metadataCache.getLossyImageCompressionMethod(
      this.props.stack
    );
    const lossyImageCompression = metadataCache.getLossyImageCompression(
      this.props.stack
    );
    const lossyImageCompressionRatio = metadataCache.getLossyImageCompressionRatio(
      this.props.stack
    );

    if (lossyImageCompression === "01" && lossyImageCompressionRatio !== "") {
      const compressionMethod = lossyImageCompressionMethod || "Lossy: ";
      const compressionRatio = parseFloat(lossyImageCompressionRatio).toFixed(
        2
      );
      return compressionMethod + compressionRatio + " : 1";
    }
    return "";
    //return 'Lossless / Uncompressed';
  }

  /**
   * @method getNumImages
   * @description 현재 출력중인 DICOM Stack의 이미지 갯수 반환
   * @return 현재 출력중인 DICOM Stack의 이미지 갯수
   * @memberof DCMTagOverlay
   */
  getNumImages() {
    const { numberOfFrames } = this.props;
    return numberOfFrames;
  }

  /**
   * @method getLayoutString
   * @description 현재 DICOM Viewer의 레이아웃 설정 사항을 리턴
   * @return 현재 DICOM Viewer의 레이아웃 설정 사항
   * @memberof DCMTagOverlay
   */
  getLayoutString() {
    let layoutString = "1x1";

    if (this.props.layoutString !== undefined) {
      layoutString = this.props.layoutString;
    }
    return layoutString;
  }

  /**
   * @method getStudyID
   * @description 현재 출력중인 DICOM Stack의 StudyID 반환
   * @return 현재 출력중인 DICOM Stack의 StudyID
   * @memberof DCMTagOverlay
   */
  getStudyID() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const studyID = metadataCache.getStudyID(this.props.stack);
    if (typeof studyID === undefined) {
      return "";
    }

    if (studyID === undefined || studyID === null) {
      return "";
    }

    return studyID;
  }

  /**
   * @method getImageComment
   * @description 현재 출력중인 DICOM Stack의 ImageComment 반환
   * @return 현재 출력중인 DICOM Stack의 ImageComment
   * @memberof DCMTagOverlay
   */
  getImageComment() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const imageComment = metadataCache.getImageComment(this.props.stack);
    if (typeof imageComment === undefined) {
      return "";
    }

    if (imageComment === undefined || imageComment === null) {
      return "";
    }

    return imageComment;
  }

  /**
   * @method getStudyComment
   * @description 현재 출력중인 DICOM Stack의 StudyComment 반환
   * @return 현재 출력중인 DICOM Stack의 StudyComment
   * @memberof DCMTagOverlay
   */
  getStudyComment() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const studyComment = metadataCache.getStudyComment(this.props.stack);
    if (typeof studyComment === undefined) {
      return "";
    }

    if (studyComment === undefined || studyComment === null) {
      return "";
    }

    return studyComment;
  }

  /**
   * @method getFieldStrenth
   * @description 현재 출력중인 DICOM Stack의 FieldStrenth 반환
   * @return 현재 출력중인 DICOM Stack의 FieldStrenth
   * @memberof DCMTagOverlay
   */
  getFieldStrenth() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const fieldStrenth = metadataCache.getFieldStrenth(this.props.stack);
    if (typeof fieldStrenth === undefined) {
      return "";
    }

    if (fieldStrenth === undefined || fieldStrenth === null) {
      return "";
    }

    //return "FS: " + parseInt(data.fieldStrenth).toFixed(1);
    return "FS: " + parseInt(fieldStrenth);
  }

  /**
   * @method getRepetitionTime
   * @description 현재 출력중인 DICOM Stack의 RepetitionTime 반환
   * @return 현재 출력중인 DICOM Stack의 RepetitionTime
   * @memberof DCMTagOverlay
   */
  getRepetitionTime() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }

    let metadataCache = new Cache.Metadata();
    const repetitionTime = metadataCache.getRepetitionTime(this.props.stack);

    if (typeof repetitionTime === undefined) {
      return "";
    }

    if (repetitionTime === undefined || repetitionTime === null) {
      return "";
    }

    return "TR: " + parseFloat(repetitionTime).toFixed(1);
  }

  /**
   * @method getEchoTime
   * @description 현재 출력중인 DICOM Stack의 EchoTime 반환
   * @return 현재 출력중인 DICOM Stack의 EchoTime
   * @memberof DCMTagOverlay
   */
  getEchoTime() {
    let data = this.props.stack;
    if (!data) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const echoTime = metadataCache.getEchoTime(this.props.stack);
    if (typeof echoTime === undefined) {
      return "";
    }

    if (echoTime === undefined || echoTime === null) {
      return "";
    }

    return "TE: " + parseFloat(echoTime).toFixed(1);
  }

  getIsNagative() {
    const { viewport } = this.props;
    if (viewport.invert) {
      return "[N]";
    }
    return "";
  }
  getMousePosition() {
    //const { mousePos } = this.props;
    const { mousePos } = this.state;

    if (
      mousePos === null ||
      typeof mousePos === undefined ||
      mousePos === undefined
    ) {
      return "";
    }

    if (mousePos.x === -1 || mousePos.y === -1) {
      return "";
    }

    if (!_.get(mousePos, "x") || !_.get(mousePos, "y")) {
      return "";
    }

    const positionText =
      "X : " + parseInt(mousePos.x) + " Y : " + parseInt(mousePos.y);
    return positionText;
  }

  getValue() {
    //const { value } = this.props;
    const { value } = this.state;
    //const value = ''

    return value;
  }

  getDefaultOrDynamicOrNone() {
    const { levelPresetName } = this.props;
    if (levelPresetName === "[default-window]") {
      return "[D]";
    }
    if (levelPresetName === "[full-dynamic]") {
      return "[F]";
    }
    return "";
  }

  getLevelPresetName() {
    const { levelPresetName } = this.props;
    if (levelPresetName === "[default-window]") {
      return "[D]";
    }
    if (levelPresetName === "[full-dynamic]") {
      return "[F]";
    }
    return levelPresetName;
  }

  /**
   * @method renderImageControls
   * @description 스크롤바 렌더링
   * @return 스크롤바 React Element
   * @memberof DCMTagOverlay
   */
  renderDCMImageSlider() {
    //const { frameRange } = this.props;
    // HACK : 한개 DCMView에 대한 포커싱 기능 구현 시, 우측 스크롤바 업데이트를 위해
    // 아래와 같이 임시로, ImageControls에서 사용하지 않는 isFocus 프로퍼티를 전달하였음.
    const { frameRange, isFocus } = this.props;
    const imageIndex = this.getFrameIndex();
    const numImages = this.getNumImages();
    const layoutString = this.getLayoutString();

    const rangeLength = frameRange["max"] - frameRange["min"] + 1;
    let frameRangeMin = this.props.frameRange["min"];
    frameRangeMin = Math.min(Math.max(frameRangeMin, 0), numImages - 1);

    //if (this.getNumImages() > 0) {
    if (numImages > 0 && rangeLength > 1) {
      return (
        <ImageControls
          imageIndex={imageIndex}
          numImages={numImages}
          //numImages={2}
          layoutString={layoutString}
          min={frameRangeMin}
          isFocus={isFocus}
          //min={0}
        />
      );
    }
  }

  getPTDisplayedHighValue(): string {
    let frameIndex = this.props.imageIndex;
    let data = this.props.stack;

    if (!data) {
      return "";
    }
    if (frameIndex === undefined || frameIndex === null) {
      return "";
    }

    if (frameIndex === -1) {
      return "";
    }
    let metadataCache = new Cache.Metadata();
    const viewport = getEnabledElementById(this.props.number).viewport;
    let windowWidth: any = 0.0;
    let windowCenter: any = 0.0;

    if (
      viewport.voi.windowWidth === null ||
      viewport.voi.windowWidth === undefined
    ) {
      windowWidth = this.getWindowWidth();
    } else {
      windowWidth = viewport.voi.windowWidth;
    }
    if (
      viewport.voi.windowCenter === null ||
      viewport.voi.windowCenter === undefined
    ) {
      windowCenter = this.getWindowCenter();
    } else {
      windowCenter = viewport.voi.windowCenter;
    }

    const image = metadataCache.getFrame(this.props.stack, frameIndex);
    //const minMax = metadataCache.getFrameMinMax(this.props.stack, frameIndex);
    const seriesMinMax = metadataCache.getSeriesMinMax(this.props.stack);
    if (!!seriesMinMax === false) {
      return "";
    }

    const seriesMaxRescaleSlope:
      | number
      | null = metadataCache.getSeriesMaxRescaleSlope(this.props.stack);
    if (seriesMaxRescaleSlope === null) {
      return "";
    }

    // const voiMax =
    //   (windowcCenter + windowWidth / 2) / image._rescaleSlope -
    //   image._rescaleIntercept;
    const voiMax = windowCenter + windowWidth / 2;
    //const maxSUV = calculateSUV(image, voiMax);
    //const pixelRange = minMax[1] - minMax[0];
    const pixelRange =
      seriesMinMax && seriesMinMax["max"] - seriesMinMax["min"];

    const percent = !!pixelRange
      ? ((windowCenter + windowWidth / 2) /
          seriesMaxRescaleSlope /
          pixelRange) *
        100
      : 0; // Divided by zero 예외처리
    //console.log(`tr slope ${seriesMaxRescaleSlope} `);
    // const percent = !!pixelRange
    //   ? (((windowCenter + windowWidth / 2) / seriesMaxRescaleSlope -
    //       image._rescaleIntercept) /
    //       pixelRange) *
    //     100
    //   : 0; // Divided by zero 예외처리
    // const percent = !!pixelRange
    //   ? (((windowCenter + windowWidth / 2) / image._rescaleSlope -
    //       image._rescaleIntercept) /
    //       pixelRange) *
    //     100
    //   : 0; // Divided by zero 예외처리

    let displayedVOIMax = 0;
    if (voiMax > 1) {
      displayedVOIMax = voiMax.toFixed(0);
    } else {
      //displayedVOIMax = voiMax.toFixed(3);
      displayedVOIMax = voiMax.toFixed(
        1 - Math.floor(Math.log(voiMax) / Math.log(8))
      );
    }

    let displayedPercent = "";
    if (percent >= 1) {
      if (Number.isInteger(percent) === true) {
        displayedPercent = percent.toFixed(0);
      } else {
        displayedPercent = percent.toFixed(1);
      }
    } else {
      displayedPercent = percent.toFixed(3);
    }

    return `Max: ${!!voiMax ? displayedVOIMax : ""} (${
      !!percent ? displayedPercent : ""
    }%)`;
    //   ? `Max: ${maxSUV.toFixed(3)} (${percent.toFixed(3)})`
    //   : "";
  }

  drawColorscalebar() {
    // 1. DCMView에 적용도니 Colormap을 획득
    const colormapBuilder = Color.getColormap("hot");
    const hotIronColormap = colormapBuilder.getColormap();
    const markers = [6.04, 0.0];
    let data = this.props.stack;
    let metadataCache = new Cache.Metadata();
    const modality = metadataCache.getModality(data);

    // fusion 대상 Element 검색
    /** 1. sourceElement 와 대응되는 Element를 검색 */
    const allEnabledElements = getEnabledElements();
    const sourceStudyInstanceUID = metadataCache.getStudyInstanceUID(data);
    const sourceModality = modality;
    const targetEnabledElement = allEnabledElements.filter((enabledElement) => {
      if (!!enabledElement.image === false) {
        return false;
      }
      const testKey = makeKey(
        enabledElement.image.seriesInstanceUID,
        enabledElement.image.modality
      );
      const testStudyInstanceUID = metadataCache.getStudyInstanceUID(testKey);
      if (sourceStudyInstanceUID !== testStudyInstanceUID) {
        return false;
      }
      if (
        (sourceModality.toLowerCase() === "pt" &&
          enabledElement.image.modality.toLowerCase() === "ct") ||
        (sourceModality.toLowerCase() === "ct" &&
          enabledElement.image.modality.toLowerCase() === "pt")
      ) {
        return true;
      }
      return false;
    });
    if (targetEnabledElement.length === 0) {
      return;
    }
    const targetElementStub = targetEnabledElement[0];
    // unit label
    let unitLabel = "";
    if (modality.toLowerCase() === "ct") {
      unitLabel = "SUVbw(g/ml)";
    }
    // min/max label
    if (modality.toLowerCase() === "pt") {
      const viewport = targetElementStub.viewport;
      let windowWidth: any = 0.0;
      let windowCenter: any = 0.0;

      if (
        viewport.voi.windowWidth === null ||
        viewport.voi.windowWidth === undefined
      ) {
        windowWidth = this.getWindowWidth();
      } else {
        windowWidth = viewport.voi.windowWidth;
      }
      if (
        viewport.voi.windowCenter === null ||
        viewport.voi.windowCenter === undefined
      ) {
        windowCenter = this.getWindowCenter();
      } else {
        windowCenter = viewport.voi.windowCenter;
      }

      let lowerBound = windowCenter - windowWidth / 2 + 1;
      let upperBound = windowCenter + windowWidth / 2 - 1;
      markers[0] = parseFloat(upperBound.toFixed(0));
      markers[1] = parseFloat(lowerBound.toFixed(0));
    } else {
      const viewport = targetElementStub.viewport;
      let windowWidth: any = 0.0;
      let windowCenter: any = 0.0;

      if (
        viewport.voi.windowWidth === null ||
        viewport.voi.windowWidth === undefined
      ) {
        windowWidth = this.getWindowWidth();
      } else {
        windowWidth = viewport.voi.windowWidth;
      }
      if (
        viewport.voi.windowCenter === null ||
        viewport.voi.windowCenter === undefined
      ) {
        windowCenter = this.getWindowCenter();
      } else {
        windowCenter = viewport.voi.windowCenter;
      }
      const maxInVOI = windowCenter + windowWidth / 2;
      const minInVOI = windowCenter - windowWidth / 2;

      const minSUV = calculateSUV(targetElementStub.image, minInVOI);
      const maxSUV = calculateSUV(targetElementStub.image, maxInVOI);
      markers[0] = parseFloat(maxSUV.toFixed(2));
      markers[1] = parseFloat(minSUV.toFixed(2));
    }
    return (
      <ColorscaleBar
        colormap={hotIronColormap}
        markers={markers}
        unitLabel={unitLabel}
      />
    );
  }

  renderDicomTags() {
    // 1. Property 획득
    const { frameRange, elementViewConfigStore, toolbarStore } = this.props;
    const numImages = this.getNumImages();
    const rangeLength = frameRange["max"] - frameRange["min"] + 1;

    // 2. Metadata text color 속성 정보 획득
    let metadataTextColor = elementViewConfigStore.getConfigAttribute(
      "metadataTextColor"
    );
    // 만약, 획득한 color 가 유효한 hex color 가 아닌 경우, 기본값으로 지정
    if (Helper.Validator.isHexColor(metadataTextColor) === false) {
      metadataTextColor = Config.elementView.metadataTextColor;
    }

    let topRightClass = cx("top-right__without-scroll", "dicomTag");
    let bottomRightClass = cx("bottom-right__without-scroll", "dicomTag");
    if (numImages > 0 && rangeLength > 1) {
      // 스크롤이 있는 경우
      topRightClass = cx("top-right", "dicomTag");
      bottomRightClass = cx("bottom-right", "dicomTag");
    }
    let enabledElement = getEnabledElementById(this.props.number);
    const dummyImageFusion = toolbarStore.getImageFusion();
    const isImageFusion = isActiveToolModeForElement(
      enabledElement.element,
      "ImageFusion"
    );
    const hasImage = !!enabledElement.image;
    let modality = "";
    if (hasImage === true) {
      modality = enabledElement.image.modality;
    }
    const isPETModality = modality.toLowerCase() === "pt";

    return (
      <React.Fragment>
        <div
          ref={(o) => {
            this.element = o;
          }}
          style={{ color: metadataTextColor }}
          className={this.isControlVisible()}
        >
          <div
            className="top-left dicomTag"
            ref={(ref: any) => (this.topLeftTagDivGroup = ref)}
          >
            <span className="top-left-item" style={this.state.style}>
              {this.getSeriesNumber() >= 0 && this.getSeriesNumber() !== ""
                ? `Se: ${this.getSeriesNumber()}`
                : ""}
            </span>
            <span className="top-left-item" style={this.state.style}>
              {this.getNumImages() > 1
                ? //? `Im : ${this.getFrameIndex() }/${this.getNumImages() - 1}`
                  `Im : ${this.getFrameIndex() + 1}/${this.getNumImages()}`
                : ""}
            </span>
            <span
              className={`top-left-item ${this.getPixelSpacingGuideTextColor()}`}
              style={this.state.style}
            >
              {this.checkPixelSpacing()}
            </span>

            <span className="compressionIndicator top-left-item">
              {this.getCompression()}
            </span>
            <span className="top-left-item">
              {isImageFusion && this.drawColorscalebar()}
            </span>
            {/* <div className="priorIndicator">{'not supported'}</div> */}
          </div>
          <div
            className={topRightClass}
            ref={(ref: any) => (this.topRightTagDivGroup = ref)}
          >
            <span className="top-right-item" style={this.state.style}>
              {this.getPatientName()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getPatientId()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {Helper.DateHelper.Format.formatDA(
                this.getPatientBirthDate(),
                "YYYY-MM-DD"
              )}{" "}
              {this.getPatientSex()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getInstitutionName()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getStudyID()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getStudyDescription()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getSeriesDescription()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getImageComment()}
            </span>
            <span className="top-right-item" style={this.state.style}>
              {this.getStudyComment()}
            </span>
          </div>
          <div
            className={bottomRightClass}
            ref={(ref: any) => (this.bottomRightTagDivGroup = ref)}
          >
            <span className="bottom-right-item" style={this.state.style}>
              {this.getXRayTubeCurrent()} {this.getKVP()}
            </span>
            <span className="bottom-right-item" style={this.state.style}>
              {this.getFieldStrenth()}
            </span>
            <span className="bottom-right-item" style={this.state.style}>
              {this.getRepetitionTime()} {this.getEchoTime()}
            </span>
            <span className="bottom-right-item" style={this.state.style}>
              {this.getAcquisitionTag()}
              {/* {formatDA(this.getAcquisitionDate(), "YYYY-MM-DD")}{" "}
              {formatTM(this.getAcquisitionTime(), "HH:MM:SS")} */}
            </span>
          </div>
          <div
            className="bottom-left dicomTag"
            ref={(ref: any) => (this.bottomLeftTagDivGroup = ref)}
          >
            <span className="bottom-left-item" style={this.state.style}>
              {!!isPETModality && this.getPTDisplayedHighValue()}{" "}
              {!!isPETModality && this.getDefaultOrDynamicOrNone()}
              {!!isPETModality && this.getIsNagative()}{" "}
            </span>
            <span className="bottom-left-item" style={this.state.style}>
              {this.getMousePosition()} {this.getValue()}
            </span>
            <span className="bottom-left-item" style={this.state.style}>
              {" "}
              {!isPETModality && this.getWWWC()}{" "}
              {!isPETModality && this.getIsNagative()}{" "}
              {!isPETModality && this.getLevelPresetName()}
            </span>

            <span className="bottom-left-item" style={this.state.style}>
              {this.getThickness()
                ? `T: ${Helper.DateHelper.Format.formatNumberPrecision(
                    this.getThickness(),
                    "2"
                  )} mm`
                : ""}
              {Helper.Validator.isNumber(this.getLocation())
                ? ` L: ${Helper.DateHelper.Format.formatNumberPrecision(
                    this.getLocation(),
                    "2"
                  )} mm`
                : ""}
            </span>
          </div>
        </div>
      </React.Fragment>
    );
  }

  /**
   * @method render
   * @description DCMTagOverlay UI 컴포넌트 렌더링
   * @return 렌더링된 DCMTagOverlay React Element
   * @memberof DCMTagOverlay
   */
  render() {
    const { show } = this.props;
    if (show === false) {
      return;
    }

    const { style } = this.props;
    if (!style || !style.fontSize) {
      return;
    }

    return (
      <React.Fragment>
        {this.renderDicomTags()}
        {this.renderDCMImageSlider()}
      </React.Fragment>
    );
  }
}

export default DCMTagOverlay;
